/* ControlNibApiMixin.js */

"use strict";
const CommonNibApiAdapter = require("../CommonNibApiMixin");
const mix = require("../util/mix");
const _ = require("lodash");
const ControlNIBObject = require("./ControlNibObject");
const platform_sdk = require("@gluenetworks/platform-sdk-remote");
const JobLogEntity = platform_sdk.entities.ws.JobLog;
const JobEntity = platform_sdk.entities.ws.Job;

let mixin = (superKlass) =>
    class ControlNibApiMixin extends mix(superKlass).with(CommonNibApiAdapter) {
        getControlObject(continuation) {
            let continuationIn = continuation;
            if (typeof continuation === "function") {
                continuationIn = continuation();
            }
            return new ControlNIBObject(this, continuationIn);
        }

        /**
         * Called when the user cancels the wizard/workflow.
         * @param continuation The associated client side execution.
         */
        userCancelled(continuation) {
            console.log("userCancelled() called.");
            if (continuation._remoteJnibInfo && continuation._remoteJnibInfo.jobId) {
                let jobId = continuation._remoteJnibInfo.jobId;
                if (continuation.jobEntity) {
                    let jobEntity = continuation.jobEntity;
                    if (_.isFunction(jobEntity.cancelJob)) {
                        console.log("userCancelled() jobEntity.cancelJob() called.");
                        jobEntity.cancelJob(jobId);
                    } else {
                        console.log(
                            "userCancelled() there was a jobId but no jobEntity.cancelJob() found."
                        );
                    }
                }
            }
        }

        /**
         * Execute a remote jnib.
         * @param continuation The associated client side execution.
         * @param startTaskMsg
         * @param jnibName
         * @param startObj
         * @returns {Promise<any>}
         */
        pmRunJNIB(continuation, startTaskMsg, jnibName, startObj, config) {
            // Hack test
            let startTaskTime = continuation.logTaskStart(startTaskMsg);
            return new Promise((resolve /*reject*/) => {
                if (!continuation.jobEntity) {
                    let errMsg = "Job request is not associated with entity.";
                    this._processError(resolve, errMsg, continuation, startTaskMsg, startTaskTime);
                } else {
                    let jobEntity = continuation.jobEntity;
                    if (_.isFunction(jobEntity.launchRemoteJnibJob)) {
                        let jobInput = {
                            startObj: startObj,
                            remoteJnib: jnibName,
                        };
                        jobEntity
                            .launchRemoteJnibJob(jobInput)
                            .then((jobId) => {
                                if (!continuation._remoteJnibInfo) {
                                    Object.defineProperty(continuation, "_remoteJnibInfo", {
                                        value: {},
                                        writable: true,
                                    });
                                }

                                continuation._remoteJnibInfo.jobId = jobId;
                                continuation._remoteJnibInfo.finishDefer = resolve;
                                continuation._remoteJnibInfo.startTaskMsg = startTaskMsg;
                                continuation._remoteJnibInfo.startTaskTime = startTaskTime;
                                continuation._remoteJnibInfo.remoteJnib = jnibName;
                                // Once the job is created we need to listen on
                                // two change feeds; the job and the jobLog
                                continuation.logs = [];
                                this._processRunJnibFinishObj(continuation, config);
                                this._processRunJnibLogFeed(continuation);
                            })
                            .catch((e) => {
                                let exceptionErrorMsg = "Unknown Error";
                                if (e.errorMsg) {
                                    exceptionErrorMsg = e.errorMsg;
                                } else if (e.message) {
                                    exceptionErrorMsg = e.message;
                                }
                                let errMsg =
                                    "Remote jnib " + jnibName + " failed: " + exceptionErrorMsg;
                                this._processError(
                                    resolve,
                                    errMsg,
                                    continuation,
                                    startTaskMsg,
                                    startTaskTime,
                                    e
                                );
                            });
                    } else {
                        let errMsg = "Entity is missing launchRemoteJnibJob function";
                        this._processError(
                            resolve,
                            errMsg,
                            continuation,
                            startTaskMsg,
                            startTaskTime
                        );
                    }
                }
            });
        }

        _processError(resolve, errMsg, continuation, taskMsg, startTaskTime, exception) {
            console.error(errMsg, exception);
            continuation.logTaskEndException(startTaskTime, taskMsg);
            resolve({errorMsg: errMsg});
        }

        _processRunJnibFinishObj(continuation, config) {
            let resolve = continuation._remoteJnibInfo.finishDefer;
            let jnibName = continuation._remoteJnibInfo.remoteJnib;
            let startTaskMsg = continuation._remoteJnibInfo.startTaskMsg;
            let startTaskTime = continuation._remoteJnibInfo.startTaskTime;
            let jobId = continuation._remoteJnibInfo.jobId;

            return Promise.resolve()
                .then(() => {
                    return JobEntity.retrieveJob(this._userSession.activeOrganization.id, jobId);
                })
                .then((jobChangeFeed) => {
                    // Create listeners on the job change feed.
                    jobChangeFeed.on("FinishObjPropertyUpdated", (finishObj) => {
                        if (finishObj.aborted || finishObj.errorMsg) {
                            console.error("Remote jnib " + jnibName + " failed: ", finishObj);
                            continuation.logTaskEndException(startTaskTime, startTaskMsg);
                        } else {
                            continuation.logTaskEndNormal(startTaskTime, startTaskMsg);
                        }
                        let jobLogChangeFeed = continuation._remoteJnibInfo.jobLogChangeFeed;
                        // Put on the next tick incase others are listening for events.
                        setTimeout(() => {
                            // continuation.jobEntity.shutdown();
                            jobLogChangeFeed.shutdown();
                        });
                        continuation._remoteJnibInfo = {};
                        if (_.get(config, "finishObjLogs", false)) {
                            finishObj.logs = continuation.logs;
                        }
                        resolve(finishObj);
                    });
                });
        }

        _processRunJnibLogFeed(continuation) {
            let jobId = continuation._remoteJnibInfo.jobId;

            let jobLogChangeFeed;
            return Promise.resolve()
                .then(() => {
                    return JobLogEntity.startJobLogChangeFeed(
                        this._userSession.activeOrganization.id,
                        jobId
                    );
                })
                .then((jlFeed) => {
                    jobLogChangeFeed = jlFeed;
                    continuation._remoteJnibInfo.jobLogChangeFeed = jlFeed;
                    return jobLogChangeFeed.whenInitialized();
                })
                .then(() => {
                    jobLogChangeFeed.on("ElementInitialized", (log) => {
                        continuation.logs.push(log);
                        continuation.newLogEvent(log);
                    });

                    jobLogChangeFeed.on("ElementAdded", (log) => {
                        continuation.logs.push(log);
                        continuation.newLogEvent(log);
                    });
                })
                .catch((e) => {
                    console.error("LogFeed error: " + e.message);
                });
        }
    };

module.exports = mixin;
